import React, { useState } from 'react';
import Modal from '../../../../components/Modal';
import { Lead } from '../hooks/useLeadsData';
import { User, Phone, Calendar, Gift, Award, MapPin, Trash2, AlertTriangle } from 'lucide-react';
import { formatBirthdate } from '../utils/dateUtils';

interface LeadDetailsModalProps {
  isOpen: boolean;
  lead: Lead | null;
  onClose: () => void;
  onOpenWhatsApp: (whatsapp: string) => void;
  formatWhatsApp: (whatsapp: string) => string;
  onDelete?: (leadId: number) => void;
}

const LeadDetailsModal: React.FC<LeadDetailsModalProps> = ({
  isOpen,
  lead,
  onClose,
  onOpenWhatsApp,
  formatWhatsApp,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!lead) return null;

  const handleDelete = () => {
    if (onDelete && lead) {
      onDelete(lead.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  // Mapear field IDs para labels
  const customFields = lead.custom_fields || [];
  const customDataEntries = lead.custom_data
    ? Object.entries(lead.custom_data)
      .map(([fieldId, value]) => {
        const field = customFields.find((f: any) => f.id === fieldId);
        const label = field?.label || fieldId;
        return { label, value, type: field?.type || 'text' };
      })
      .filter(entry => entry.value)
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do Cliente"
      size="lg"
    >
      <div className="space-y-6">
        {/* Informações Principais */}
        <div className="bg-creme-light border border-tinta/10 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-bordo border border-bordo-dark/30 rounded-full flex items-center justify-center flex-shrink-0">
                <User size={32} className="text-creme" />
              </div>
              <div>
                <h3 className="font-display text-2xl font-black text-tinta leading-tight">{lead.customer_name}</h3>
                <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mt-1">Cliente #{lead.id}</p>
              </div>
            </div>
            {lead.won ? (
              <div className="bg-musgo text-creme border border-musgo-dark/30 px-4 py-2 rounded-full font-bold text-sm inline-flex items-center space-x-2 self-start sm:self-auto">
                <Award size={18} />
                <span>Ganhou Prêmio!</span>
              </div>
            ) : (
              <div className="bg-tinta/8 text-tinta-soft border border-tinta/15 px-4 py-2 rounded-full font-bold text-sm inline-flex items-center space-x-2 self-start sm:self-auto italic">
                <Award size={18} />
                <span>Não foi dessa vez</span>
              </div>
            )}
          </div>
        </div>

        {/* Grid de Informações */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* WhatsApp */}
          {
            lead.whatsapp && (
              <div className="bg-white border border-tinta/10 rounded-xl p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="bg-musgo/10 border border-musgo/20 p-2 rounded-lg flex-shrink-0">
                      <Phone size={20} className="text-musgo" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">WhatsApp</p>
                      <p className="text-sm font-semibold text-tinta truncate">
                        {formatWhatsApp(lead.whatsapp)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onOpenWhatsApp(lead.whatsapp)}
                    className="px-3 py-1.5 bg-musgo hover:bg-musgo-dark text-creme text-xs font-bold rounded-lg transition-colors flex-shrink-0"
                  >
                    Abrir
                  </button>
                </div>
              </div>
            )
          }

          {/* Data de Nascimento */}
          {lead.customer_birthdate && (
            <div className="bg-white border border-tinta/10 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <div className="bg-mostarda/20 border border-mostarda/40 p-2 rounded-lg flex-shrink-0">
                  <Calendar size={20} className="text-mostarda-dark" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Data de Nascimento</p>
                  <p className="text-sm font-semibold text-tinta">
                    {formatBirthdate(lead.customer_birthdate, {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Sorteio */}
          <div className="bg-white border border-tinta/10 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-bordo/10 border border-bordo/20 p-2 rounded-lg flex-shrink-0">
                <MapPin size={20} className="text-bordo" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Sorteio</p>
                <p className="text-sm font-semibold text-tinta truncate">{lead.wheel_name}</p>
              </div>
            </div>
          </div>

          {/* Data de Participação */}
          <div className="bg-white border border-tinta/10 rounded-xl p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-musgo/10 border border-musgo/20 p-2 rounded-lg flex-shrink-0">
                <Calendar size={20} className="text-musgo" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Data de Participação</p>
                <p className="text-sm font-semibold text-tinta">
                  {new Date(lead.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Prêmio Ganho */}
        {lead.won && (
          <div className="bg-musgo/10 border border-musgo/30 rounded-xl p-6">
            {/* Múltiplos Produtos */}
            {lead.product_ids && lead.product_ids.length > 1 ? (
              <div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-musgo border border-musgo-dark/30 p-3 rounded-full flex-shrink-0">
                    <Gift size={28} className="text-creme" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-musgo">Prêmios Ganhos</p>
                    <p className="font-display text-2xl font-black text-tinta">
                      {lead.product_ids.length} Prêmios
                    </p>
                  </div>
                </div>

                {/* Lista de Prêmios */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {lead.product_names?.map((name, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-musgo/20 shadow-sm"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-musgo text-creme rounded-full flex items-center justify-center font-bold text-sm">
                        {index + 1}
                      </div>
                      {lead.product_images?.[index] && (
                        <img
                          src={lead.product_images[index]}
                          alt={name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-tinta truncate">{name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Produto Único */
              <div className="flex items-center space-x-4">
                <div className="bg-musgo border border-musgo-dark/30 p-3 rounded-full flex-shrink-0">
                  <Gift size={28} className="text-creme" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-musgo">Prêmio Ganho</p>
                  <p className="font-display text-2xl font-black text-tinta truncate">{lead.product_name}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Não Ganhou Prêmio */}
        {!lead.won && (
          <div className="bg-creme-light border border-tinta/10 rounded-xl p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-tinta/8 border border-tinta/15 p-3 rounded-full flex-shrink-0">
                <Gift size={28} className="text-tinta-soft" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">Status</p>
                <p className="text-base font-semibold text-tinta italic">Não foi dessa vez nesta tentativa</p>
              </div>
            </div>
          </div>
        )}

        {/* Dados Personalizados */}
        {customDataEntries.length > 0 && (
          <div className="border-t border-tinta/10 pt-6">
            <h4 className="font-display text-lg font-bold text-tinta mb-4 flex items-center gap-2">
              <span className="bg-bordo/10 text-bordo border border-bordo/20 px-3 py-1 rounded-full text-xs font-bold">
                {customDataEntries.length}
              </span>
              Informações Adicionais
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {customDataEntries.map((entry, idx) => (
                <div key={idx} className="bg-creme-light border border-tinta/10 rounded-lg p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-tinta-soft mb-1">{entry.label}</p>
                  <p className="text-sm font-semibold text-tinta break-words">
                    {String(entry.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex justify-between items-center pt-4 border-t border-tinta/10">
          {onDelete && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-bordo hover:bg-bordo-dark text-creme rounded-lg transition-colors text-sm font-bold flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Excluir Lead
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 border border-tinta/15 text-tinta-soft bg-white hover:bg-creme-light hover:text-tinta rounded-lg transition-colors text-sm font-bold ml-auto"
          >
            Fechar
          </button>
        </div>

        {/* Confirmação de Exclusão */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-tinta/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-tinta/10 rounded-xl shadow-lg p-6 max-w-md w-full">
              <h3 className="font-display text-xl font-bold text-tinta mb-3">Confirmar Exclusão</h3>
              <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30 mb-4">
                <AlertTriangle className="w-5 h-5 text-bordo mt-0.5 flex-shrink-0" />
                <p className="text-sm text-tinta leading-relaxed">
                  Tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita.
                  {lead.won && ' O estoque dos produtos ganhos será devolvido.'}
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-tinta/15 text-tinta-soft bg-white hover:bg-creme-light hover:text-tinta rounded-lg transition-colors text-sm font-bold"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-bordo hover:bg-bordo-dark text-creme rounded-lg transition-colors text-sm font-bold"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LeadDetailsModal;
